import React, { ComponentProps, useCallback, useContext } from 'react'

import { pxToRem } from '@/shared/styles/themes'
import { MenuItem, styled } from '@mui/material'

import { DropdownContext } from './DropdownContext'

export type DropdownItemProps = Omit<ComponentProps<typeof StyledDropdownItem>, 'disabled'> & {
  children: React.ReactNode
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onSelect?: (event: any) => void
  disabled?: boolean
  isSelected?: boolean
}

export const DropdownItem: React.FC<DropdownItemProps> = ({ children, onSelect, isSelected, ...props }) => {
  const dropdownContext = useContext(DropdownContext)
  
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const onDropdownClick = useCallback((event: any) => {
    event.preventDefault()
    event.stopPropagation()

    if (onSelect) {
      onSelect(event)
    }

    if (dropdownContext.onSelectItem) {
      dropdownContext.onSelectItem(event.target?.getAttribute('data-value'))
    }

    if (dropdownContext.setDropdownOpen) {
      dropdownContext.setDropdownOpen(false)
    }
  }, [dropdownContext, onSelect])

  return (
    <StyledDropdownItem
      disableRipple
      disableTouchRipple
      disableGutters
      onClick={onDropdownClick}
      $isSelected={isSelected}
      {...props}
    >
      {children}
    </StyledDropdownItem>
  )
}

export const StyledDropdownItem = styled(MenuItem, {
  shouldForwardProp: (prop) => prop !== '$disabled' && prop !== '$isSelected',
}) <{ $isSelected?: boolean }>`
  margin: 0;
  z-index: 2;
  min-height: ${pxToRem(32)};
  white-space: nowrap;
  text-overflow: ellipsis;
  color: ${props => props.theme.palette.text.primary};
  font-size: ${props => props.theme.typography.pxToRem(13)};
  padding: ${props => props.theme.typography.pxToRem(5)} ${props => props.theme.typography.pxToRem(8)};

  &:hover {
    background-color: ${props => props.theme.palette.action.hover};

    &:after {
      opacity: 1;
    }
  }

  ${(props) => props.$isSelected && `
    background-color: ${props.theme.palette.action.hover};

    &:hover {
      background-color: ${props.theme.palette.action.hover};
    }
  `}

  & .MuiSvgIcon-root {
    margin-right: ${(props) => props.theme.typography.pxToRem(6)};
    font-size: ${(props) => props.theme.typography.pxToRem(16)};
  }
`
